import { X } from "lucide-react";

import eligibilityDesktop from "@assets/images/shop/hsa-desktop.webp";
import eligibilityMobile from "@assets/images/shop/hsa-mobile.webp";
import eligibilityTablet from "@assets/images/shop/hsa-tablet.webp";
import Dialog from "@/blueprint/components/Dialog/Dialog";
import ResponsiveArt, {
  type ArtSpec,
} from "@/blueprint/components/ResponsiveArt/ResponsiveArt";
import Text from "@/blueprint/components/Text/Text";
import { CONFIGURATOR } from "@/views/shop/shopContent";

type Props = { open: boolean; onClose: () => void };

const { eligibility } = CONFIGURATOR;

/* Small desktop and desktop share one frame in Figma. Quality 90 because 75
   visibly smooths the fabric; the idle preload keeps the extra bytes off the
   critical path. */
export const ELIGIBILITY_ART: ArtSpec = {
  sources: {
    mobile: eligibilityMobile,
    tablet: eligibilityTablet,
    sd: eligibilityDesktop,
    desktop: eligibilityDesktop,
  },
  sizes: "(min-width: 1280px) 484px, 100vw",
  quality: 90,
};

const HsaFsaEligibilityModal = ({ open, onClose }: Props) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-label={eligibility.dialogLabel}
    dismissOnBackdropClick
    className="fixed inset-0 z-[100] flex items-center justify-center bg-tint-pure-black-20 p-[18px] tab:p-[32px] sd:p-[64px]"
  >
    <div className="relative flex w-full max-w-[366px] flex-col gap-[20px] rounded-m bg-surface-dialog p-[20px] tab:max-w-[662px] tab:gap-[24px] tab:p-[24px] sd:max-w-[1100px] sd:flex-row sd:gap-[40px] sd:p-[48px]">
      <ResponsiveArt
        {...ELIGIBILITY_ART}
        alt=""
        className="w-full rounded-s object-cover [aspect-ratio:978/684] tab:[aspect-ratio:1842/1020] sd:w-[484px] sd:shrink-0 sd:self-stretch sd:[aspect-ratio:auto]"
      />

      <div className="flex flex-col gap-[20px] tab:gap-[24px] sd:w-[480px] sd:justify-center">
        <div className="flex flex-col gap-[12px]">
          <Text variant="h5" as="h2">
            {eligibility.heading}
          </Text>
          <ol className="flex list-decimal flex-col gap-[12px] pl-[21px]">
            {eligibility.steps.map((step) => (
              <Text key={step} variant="paragraph-md" as="li">
                {step}
              </Text>
            ))}
          </ol>
        </div>
        <Text
          variant="paragraph-sm"
          as="p"
          tone="secondary"
          className="px-[13px]"
        >
          <strong className="font-bold">{eligibility.footnoteLead}</strong>{" "}
          {eligibility.footnote}
        </Text>
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={eligibility.closeLabel}
        className="absolute right-[20px] top-[20px] flex size-[64px] items-center justify-center p-[16px] tab:right-[24px] tab:top-[24px] tab:size-[72px] tab:p-[20px] sd:right-0 sd:top-0 sd:size-[96px] sd:p-[32px]"
      >
        <X
          aria-hidden
          className="size-[32px] text-brand-white sd:text-brand-black"
        />
      </button>
    </div>
  </Dialog>
);

export default HsaFsaEligibilityModal;
